"use client";

/**
 * The affiliate portal's opening screen. It replaces the old blank page with "Loading your portal…": the portal's
 * own frame appears at once (top bar, the violet balance card in its real place, the stat tiles), and inside the
 * card a short live sequence says what is happening while the numbers arrive. When they do, the page swaps in the
 * real card in the same spot, so it reads as the portal assembling itself rather than a page change.
 *
 * Used twice so the move from the website is seamless: as a full-screen cover the moment "Go to your affiliate
 * dashboard" is pressed (`cover`), and by the portal page itself until its numbers arrive. Fast connections never
 * see the cover (it fades in after a short delay); slow ones see motion and progress, never a stuck screen.
 * Animations change only transform and opacity, and stop for reduced motion.
 */
import Link from "next/link";
import { useEffect, useState } from "react";
import { AlertTriangle, LogOut, RotateCcw, ShieldCheck, Sparkles, Wallet } from "lucide-react";

const STEPS = [
  { at: 0, text: "Signing you in securely" },
  { at: 1100, text: "Fetching your balance" },
  { at: 2400, text: "Counting your referrals" },
  { at: 4200, text: "Almost there" },
  { at: 7500, text: "Slow connection — still loading" },
];

export function PortalHeaderFrame({ right }: { right?: React.ReactNode }) {
  return (
    <header className="sticky top-0 z-20 border-b border-violet-deep/10 bg-white/90 backdrop-blur-md">
      <div className="mx-auto flex max-w-6xl items-center justify-between gap-3 px-4 py-3 sm:px-5 sm:py-4">
        <Link href="/" className="flex min-w-0 items-center gap-2.5 sm:gap-3.5" aria-label="Rankbist home">
          {/* eslint-disable-next-line @next/next/no-img-element -- the same wordmark as the site's navbar */}
          <img src="/rankbist.png" alt="Rankbist" width={1781} height={375} className="h-[20px] w-auto flex-shrink-0 sm:h-[26px]" />
          <span className="h-6 w-px flex-shrink-0 bg-violet-deep/15 sm:h-8" aria-hidden />
          <span className="truncate text-violet-deep text-[1.15rem] font-extrabold leading-none tracking-[-0.02em] sm:text-[1.6rem]">Affiliate</span>
        </Link>
        <div className="flex flex-shrink-0 items-center gap-2 sm:gap-3">
          {right ?? (
            <>
              <span className="rb-skeleton hidden h-3.5 w-28 rounded-md md:block" />
              <span className="rb-skeleton h-8 w-8 rounded-full sm:h-9 sm:w-9" />
            </>
          )}
        </div>
      </div>
    </header>
  );
}

function useElapsed(active: boolean) {
  const [ms, setMs] = useState(0);
  useEffect(() => {
    if (!active) return;
    const start = performance.now();
    const id = window.setInterval(() => setMs(performance.now() - start), 200);
    return () => window.clearInterval(id);
  }, [active]);
  return ms;
}

/** The violet card's opening state: orbiting wallet, live step text and a progress line. */
function OpeningCard({ error, onRetry, onSignOut }: { error?: string | null; onRetry?: () => void; onSignOut?: () => void }) {
  const ms = useElapsed(!error);
  const step = [...STEPS].reverse().find((s) => ms >= s.at) ?? STEPS[0];
  // Eases toward 92% and never claims to be done before the numbers really are.
  const progress = Math.min(0.92, 1 - Math.exp(-ms / 2600));

  return (
    <section
      className="relative overflow-hidden rounded-[24px] bg-gradient-to-br from-[#A488FF] via-violet to-violet-deep p-5 text-white shadow-[0_30px_70px_-28px_rgba(80,50,180,0.7)] sm:rounded-[32px] sm:p-8"
      aria-live="polite"
    >
      <div className="pointer-events-none absolute -right-16 -top-20 h-64 w-64 rounded-full bg-[radial-gradient(circle,rgba(255,255,255,0.18),transparent_70%)]" />
      <div className="pointer-events-none absolute -bottom-24 left-10 h-56 w-56 rounded-full bg-[radial-gradient(circle,rgba(255,120,200,0.25),transparent_70%)]" />

      <div className="relative grid min-h-[230px] grid-cols-1 items-center gap-8 lg:grid-cols-[minmax(0,1.1fr)_minmax(0,1fr)]">
        {error ? (
          <div>
            <span className="inline-flex h-12 w-12 items-center justify-center rounded-2xl bg-white/15 ring-1 ring-white/25">
              <AlertTriangle size={22} />
            </span>
            <h1 className="mt-4 text-[1.35rem] font-extrabold leading-tight sm:text-[1.6rem]">We couldn&apos;t open your portal</h1>
            <p className="mt-1.5 max-w-md text-[13.5px] leading-relaxed text-white/85">{error}</p>
            <div className="mt-5 flex flex-wrap gap-2">
              {onRetry && (
                <button onClick={onRetry} className="inline-flex h-11 items-center gap-2 rounded-xl bg-white px-5 text-[14px] font-bold text-violet-deep transition hover:-translate-y-px">
                  <RotateCcw size={16} /> Try again
                </button>
              )}
              {onSignOut && (
                <button onClick={onSignOut} className="inline-flex h-11 items-center gap-2 rounded-xl px-4 text-[13.5px] font-semibold text-white/85 ring-1 ring-white/30 transition hover:bg-white/10">
                  <LogOut size={15} /> Sign out
                </button>
              )}
            </div>
          </div>
        ) : (
          <div className="flex items-center gap-5">
            <span className="relative grid h-[72px] w-[72px] flex-shrink-0 place-items-center">
              <span className="rb-aff-ring absolute inset-0 rounded-full border-2 border-white/25 border-t-white" aria-hidden />
              <span className="rb-aff-glow absolute inset-2 rounded-full bg-white/15" aria-hidden />
              <Wallet size={26} className="relative" />
            </span>
            <div className="min-w-0">
              <div className="inline-flex items-center gap-1.5 text-[11.5px] font-semibold uppercase tracking-wide text-white/75">
                <ShieldCheck size={13} /> Affiliate portal
              </div>
              <h1 className="mt-1 text-[1.35rem] font-extrabold leading-tight sm:text-[1.7rem]">Opening your portal</h1>
              <p key={step.text} className="rb-aff-step mt-1 text-[13.5px] font-medium text-white/85">{step.text}…</p>
              <div className="mt-4 h-1.5 w-[min(260px,60vw)] overflow-hidden rounded-full bg-white/15">
                <div
                  className="h-full origin-left rounded-full bg-white transition-transform duration-300 ease-out"
                  style={{ transform: `scaleX(${progress.toFixed(3)})` }}
                />
              </div>
            </div>
          </div>
        )}

        {/* Where the referral link card will be: its shape, quietly breathing. */}
        <div className="hidden rounded-2xl bg-white/10 p-5 ring-1 ring-white/15 lg:block" aria-hidden>
          <div className="inline-flex items-center gap-1.5 text-[12px] font-semibold uppercase tracking-wide text-white/70"><Sparkles size={13} /> Your referral link</div>
          <div className="rb-boot-pulse mt-3 h-10 rounded-xl bg-white/15" />
          <div className="mt-4 grid grid-cols-3 gap-2">
            {[0, 1, 2].map((i) => (
              <div key={i} className="rb-boot-pulse h-[68px] rounded-xl bg-white/10" style={{ animationDelay: `${i * 140}ms` }} />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

/** The whole portal page in its opening state (top bar + violet card + stat tiles + section cards). */
export function PortalOpening({
  error,
  onRetry,
  onSignOut,
  cover = false,
}: {
  error?: string | null;
  onRetry?: () => void;
  onSignOut?: () => void;
  /** Full-screen cover over the website while the portal page itself loads. */
  cover?: boolean;
}) {
  return (
    <main
      className={cover ? "rb-aff-cover fixed inset-0 z-[1400] overflow-hidden bg-[#f6f4fd] text-ink" : "min-h-screen bg-[#f6f4fd] text-ink"}
      aria-busy={!error}
      aria-label="Opening your affiliate portal"
    >
      {!error && <div className="rb-boot-bar" aria-hidden />}
      <PortalHeaderFrame />
      <div className="mx-auto max-w-6xl space-y-5 px-4 py-5 sm:space-y-6 sm:px-5 sm:py-7">
        <OpeningCard error={error} onRetry={onRetry} onSignOut={onSignOut} />
        <div className="grid grid-cols-2 gap-4 lg:grid-cols-4" aria-hidden>
          {[0, 1, 2, 3].map((i) => (
            <div key={i} className="rounded-2xl border border-violet-deep/10 bg-white p-4">
              <span className="rb-skeleton block h-9 w-9 rounded-xl" />
              <span className="rb-skeleton mt-3 block h-6 w-16 rounded-md" />
              <span className="rb-skeleton mt-2 block h-3 w-24 rounded" />
            </div>
          ))}
        </div>
        <div className="grid gap-6 lg:grid-cols-2" aria-hidden>
          {[0, 1].map((i) => (
            <div key={i} className="rounded-3xl border border-violet-deep/10 bg-white p-6">
              <span className="rb-skeleton block h-4 w-32 rounded" />
              <span className="rb-skeleton mt-3 block h-3 w-3/4 rounded" />
              <span className="rb-skeleton mt-5 block h-24 w-full rounded-2xl" />
            </div>
          ))}
        </div>
      </div>
    </main>
  );
}

/** A small placeholder for one portal section while its own list loads. */
export function SectionLoading({ rows = 3 }: { rows?: number }) {
  return (
    <div className="mt-4 space-y-2" aria-busy="true">
      {Array.from({ length: rows }, (_, i) => (
        <div key={i} className="rb-skeleton h-12 w-full rounded-2xl" />
      ))}
    </div>
  );
}
